import { put } from '@vercel/blob';

/**
 * Upload a file to Vercel Blob storage
 * @param {File} file - The file to upload
 * @param {string} filename - Optional custom filename
 * @returns {Promise<string>} - URL of the uploaded file
 */
export async function uploadToBlob(file, filename = null) {
  try {
    // Generate a unique filename if not provided
    const uniqueFilename = filename || `${Date.now()}-${file.name}`;
    
    // Upload to Vercel Blob
    const { url } = await put(uniqueFilename, file, {
      access: 'public',
      token: process.env.REACT_APP_BLOB_READ_WRITE_TOKEN || 'vercel_blob_rw_8Khq3dc1D8IiwPbo_wwG0gVo83DoJnjjtesZcj6Qb0p01s6'
    });
    
    return url;
  } catch (error) {
    console.error('Error uploading to Vercel Blob:', error);
    throw error;
  }
}

/**
 * Upload multiple files to Vercel Blob storage
 * @param {File[]} files - Array of files to upload
 * @param {string} prefix - Optional prefix for filenames
 * @returns {Promise<string[]>} - Array of URLs of the uploaded files
 */
export async function uploadMultipleToBlob(files, prefix = '') {
  try {
    const uploadPromises = Array.from(files).map(async (file, index) => {
      const filename = `${prefix}${prefix ? '-' : ''}${Date.now()}-${index}-${file.name}`;
      return uploadToBlob(file, filename);
    });
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple files to Vercel Blob:', error);
    throw error;
  }
}

/**
 * Extract filename from Vercel Blob URL
 * @param {string} url - Vercel Blob URL
 * @returns {string} - Extracted filename
 */
export function getFilenameFromBlobUrl(url) {
  if (!url) return '';
  
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    return pathParts[pathParts.length - 1];
  } catch (error) {
    console.error('Error extracting filename from Blob URL:', error);
    return '';
  }
}

/**
 * Check if a URL is a Vercel Blob URL
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL is a Vercel Blob URL
 */
export function isBlobUrl(url) {
  if (!url) return false;
  
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.includes('vercel-blob.com');
  } catch (error) {
    return false;
  }
}
